/**
 * Job Routing Row
 *
 * Individual job row with:
 * - Checkbox for selection
 * - Job number, description, qty
 * - Inline routing visualization
 * - Status badge and action buttons
 */

import { useState } from "react";
import RoutingTimeline from "./RoutingTimeline";
import { apiClient } from "../../api/apiClient";

// PM list for notifications
const PM_LIST = [
  { name: "Amanda Billiter", email: "amanda.billiter@3dcolor.com" },
  { name: "Trent Parker", email: "trent.p@3dcolor.com" },
  { name: "Gary Hempel", email: "gary.hempel@3dcolor.com" },
  { name: "Janet Phan", email: "janet.phan@3dcolor.com" },
  { name: "Chris Baker", email: "chris.baker@3dcolor.com" },
  { name: "Jessica Lynch", email: "jessica.lynch@3dcolor.com" },
];

// PA list for notifications (PM → PA)
const PA_LIST = [
  { name: "Shane Schwalbach", email: "shane.schwalbach@3dcolor.com" },
  { name: "Jason F", email: "jason.f@3dcolor.com" },
  { name: "Gabby Pelgen", email: "gabby.pelgen@3dcolor.com" },
  { name: "Chris Osborne", email: "chris.osborne@3dcolor.com" },
  { name: "Julie Schwey", email: "julie.schwey@3dcolor.com" },
];

export default function JobRoutingRow({
  job,
  isSelected,
  onToggle,
  selectedWorkCenters,
  formatElapsed,
  onStart,
  onPause,
  onResume,
  onComplete,
  quoteNumber,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [piecesFinished, setPiecesFinished] = useState("");
  const [showProofReadyModal, setShowProofReadyModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [showReprintModal, setShowReprintModal] = useState(false);
  const [selectedPM, setSelectedPM] = useState("");
  const [selectedPA, setSelectedPA] = useState("");
  const [reprintReason, setReprintReason] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [notificationType, setNotificationType] = useState("");

  const isRunning = job.activeSession?.state === "running";
  const isPaused = job.activeSession?.state === "paused";
  const isActive = isRunning || isPaused;
  const hasMyWork = job.myOperations.length > 0;

  // Get the default operation for starting
  const getDefaultOperation = () => {
    if (job.myOperations.length > 0) {
      return job.myOperations[0];
    }
    return null;
  };

  const handleStart = () => {
    const op = getDefaultOperation();
    if (op) {
      onStart(job, op.operationCode);
    }
  };

  const handleComplete = () => {
    const pieces = parseInt(piecesFinished, 10) || job.qty || 0;
    onComplete(job.jobId, pieces);
    setShowCompleteModal(false);
    setPiecesFinished("");
  };

  const handleNotifyProofReady = async () => {
    if (!selectedPM) return;

    setSendingNotification(true);
    setNotificationType("proof_ready");
    try {
      await apiClient.post("/api/notifications/proof-ready", {
        recipientEmail: selectedPM,
        jobNumber: job.jobNumber,
        quoteNumber: quoteNumber,
        jobDescription: job.description || job.partNumber,
      });
      setNotificationSent(true);
      setTimeout(() => {
        setShowProofReadyModal(false);
        setSelectedPM("");
        setNotificationSent(false);
        setNotificationType("");
      }, 1500);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Failed to send notification. Please try again.");
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyApproved = async () => {
    if (!selectedPA) return;

    setSendingNotification(true);
    setNotificationType("proof_approved");
    try {
      await apiClient.post("/api/notifications/proof-approved", {
        recipientEmail: selectedPA,
        jobNumber: job.jobNumber,
        quoteNumber: quoteNumber,
        jobDescription: job.description || job.partNumber,
      });
      setNotificationSent(true);
      setTimeout(() => {
        setShowApprovedModal(false);
        setSelectedPA("");
        setNotificationSent(false);
        setNotificationType("");
      }, 1500);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Failed to send notification. Please try again.");
    } finally {
      setSendingNotification(false);
    }
  };

  const handleNotifyReprint = async () => {
    if (!selectedPA) return;

    setSendingNotification(true);
    setNotificationType("reprint_needed");
    try {
      await apiClient.post("/api/notifications/reprint-needed", {
        recipientEmail: selectedPA,
        jobNumber: job.jobNumber,
        quoteNumber: quoteNumber,
        reason: reprintReason,
      });
      setNotificationSent(true);
      setTimeout(() => {
        setShowReprintModal(false);
        setSelectedPA("");
        setReprintReason("");
        setNotificationSent(false);
        setNotificationType("");
      }, 1500);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Failed to send notification. Please try again.");
    } finally {
      setSendingNotification(false);
    }
  };

  // Status badge
  const getStatusBadge = () => {
    if (isRunning) {
      return (
        <span className="px-2 py-0.5 text-xs bg-green-600 text-gray-900 rounded-full">
          Running
        </span>
      );
    }
    if (isPaused) {
      return (
        <span className="px-2 py-0.5 text-xs bg-yellow-600 text-gray-900 rounded-full">
          Paused
        </span>
      );
    }
    if (job.status === "blocked") {
      return (
        <span className="px-2 py-0.5 text-xs bg-gray-600 text-gray-700 rounded-full">
          Waiting
        </span>
      );
    }
    if (job.status === "complete") {
      return (
        <span className="px-2 py-0.5 text-xs bg-emerald-600/50 text-emerald-300 rounded-full">
          Complete
        </span>
      );
    }
    if (hasMyWork) {
      return (
        <span className="px-2 py-0.5 text-xs bg-indigo-600 text-gray-900 rounded-full">
          Ready
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <div
        className={`border-b border-gray-200 ${
          isActive
            ? "bg-green-900/20"
            : hasMyWork
            ? "bg-indigo-900/10"
            : ""
        }`}
      >
        {/* Main Row */}
        <div className="p-3 flex items-center gap-3">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggle}
            disabled={isActive}
            className={`h-4 w-4 rounded border-gray-300 bg-gray-700 text-indigo-500 focus:ring-indigo-500 ${
              isActive ? "opacity-50 cursor-not-allowed" : ""
            }`}
          />

          {/* Job Info */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-1 text-left min-w-0"
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-gray-900">
                {job.jobNumber}
              </span>
              {getStatusBadge()}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {job.description || job.partNumber || "No description"}
            </div>
          </button>

          {/* Qty */}
          <div className="text-center w-14">
            <div className="text-lg font-bold text-yellow-600">{job.qty}</div>
            <div className="text-[10px] text-gray-500">QTY</div>
          </div>

          {/* Timer or Action */}
          <div className="w-28 text-right">
            {isActive ? (
              <div className="flex items-center justify-end gap-2">
                <span
                  className={`text-lg font-mono ${
                    isRunning ? "text-green-400" : "text-yellow-400"
                  }`}
                >
                  {formatElapsed(job.activeSession)}
                </span>
                {isRunning ? (
                  <button
                    onClick={() => onPause(job.jobId)}
                    className="p-1 text-yellow-400 hover:text-yellow-600"
                    title="Pause"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => onResume(job.jobId)}
                    className="p-1 text-green-400 hover:text-green-600"
                    title="Resume"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => setShowCompleteModal(true)}
                  className="p-1 text-blue-400 hover:text-blue-600"
                  title="Complete"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ) : hasMyWork ? (
              <button
                onClick={handleStart}
                className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-gray-900 text-sm font-medium rounded-lg transition-colors"
              >
                START
              </button>
            ) : (
              <span className="text-xs text-gray-500">—</span>
            )}
          </div>

          {/* Expand Toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-gray-500 hover:text-gray-900"
          >
            <svg
              className={`w-5 h-5 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Expanded: Routing Timeline */}
        {expanded && (
          <div className="px-3 pb-3 pt-1">
            <RoutingTimeline
              routing={job.routing}
              selectedWorkCenters={selectedWorkCenters}
            />
            {job.myOperations.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                <span className="text-indigo-400">My operations:</span>{" "}
                {job.myOperations.map((op) => op.operationCode).join(", ")}
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => setShowProofReadyModal(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-gray-900 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                title="Notify PM that PDF proof is ready"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PDF Proof Ready
              </button>
              <button
                onClick={() => setShowApprovedModal(true)}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-gray-900 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                title="Notify PA that proof is approved"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                PDF Approved
              </button>
              <button
                onClick={() => setShowReprintModal(true)}
                className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-gray-900 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                title="Request reprint from PA"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reprint Needed
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Job</h3>

            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">
                {job.description || job.jobNumber}
              </p>
              {isActive && (
                <p className="text-2xl font-mono font-bold text-green-400">
                  {formatElapsed(job.activeSession)}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">
                Pieces Finished
              </label>
              <input
                type="number"
                value={piecesFinished}
                onChange={(e) => setPiecesFinished(e.target.value)}
                placeholder={job.qty?.toString() || "0"}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-lg text-center focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setPiecesFinished("");
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-gray-900 font-bold rounded-xl transition-colors"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Proof Ready Modal */}
      {showProofReadyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-sm">
            {notificationSent ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-3">✓</div>
                <h3 className="text-xl font-bold text-green-400">Notification Sent!</h3>
                <p className="text-sm text-gray-500 mt-2">PM has been notified.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">PDF Proof Ready</h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">
                    Job: <span className="text-gray-900">{job.jobNumber}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {job.description || job.partNumber || "No description"}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-500 mb-2">
                    Notify PM
                  </label>
                  <select
                    value={selectedPM}
                    onChange={(e) => setSelectedPM(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select a PM...</option>
                    {PM_LIST.map((pm) => (
                      <option key={pm.email} value={pm.email}>
                        {pm.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowProofReadyModal(false);
                      setSelectedPM("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNotifyProofReady}
                    disabled={!selectedPM || sendingNotification}
                    className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-colors"
                  >
                    {sendingNotification ? "Sending..." : "Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* PDF Approved Modal */}
      {showApprovedModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-sm">
            {notificationSent && notificationType === "proof_approved" ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-3">✓</div>
                <h3 className="text-xl font-bold text-green-400">Notification Sent!</h3>
                <p className="text-sm text-gray-500 mt-2">PA has been notified of approval.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">PDF Approved</h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">
                    Job: <span className="text-gray-900">{job.jobNumber}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {job.description || job.partNumber || "No description"}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-500 mb-2">
                    Notify Production Artist
                  </label>
                  <select
                    value={selectedPA}
                    onChange={(e) => setSelectedPA(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-green-500"
                  >
                    <option value="">Select a PA...</option>
                    {PA_LIST.map((pa) => (
                      <option key={pa.email} value={pa.email}>
                        {pa.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowApprovedModal(false);
                      setSelectedPA("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNotifyApproved}
                    disabled={!selectedPA || sendingNotification}
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-colors"
                  >
                    {sendingNotification ? "Sending..." : "Approve"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reprint Needed Modal */}
      {showReprintModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-sm">
            {notificationSent && notificationType === "reprint_needed" ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-3">✓</div>
                <h3 className="text-xl font-bold text-green-400">Notification Sent!</h3>
                <p className="text-sm text-gray-500 mt-2">PA has been notified of reprint request.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Reprint Needed</h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">
                    Job: <span className="text-gray-900">{job.jobNumber}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {job.description || job.partNumber || "No description"}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-gray-500 mb-2">
                    Notify Production Artist
                  </label>
                  <select
                    value={selectedPA}
                    onChange={(e) => setSelectedPA(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Select a PA...</option>
                    {PA_LIST.map((pa) => (
                      <option key={pa.email} value={pa.email}>
                        {pa.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-500 mb-2">
                    Reason for Reprint
                  </label>
                  <textarea
                    value={reprintReason}
                    onChange={(e) => setReprintReason(e.target.value)}
                    placeholder="Describe why the reprint is needed..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowReprintModal(false);
                      setSelectedPA("");
                      setReprintReason("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNotifyReprint}
                    disabled={!selectedPA || sendingNotification}
                    className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-colors"
                  >
                    {sendingNotification ? "Sending..." : "Request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
